import { useState, useEffect, useRef, useCallback } from "react";
import {
  CAMPAIGN_STATES,
  DEFAULT_PAGE_LENGTH,
} from "../utils/campaignFilters";

const SEARCH_DEBOUNCE_MS = 500;

const EMPTY_FILTERS = { dateFrom: null, dateTo: null, clientId: null };

const useCampaignsList = ({
  user,
  campaigns,
  totalCampaigns,
  fetching,
  pageCampaigns,
  getCampaigns,
  setPageCampaigns,
  initialTab = "0",
  states = CAMPAIGN_STATES,
}) => {
  const [activeTab, setActiveTab] = useState(initialTab);
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const [filters, setFilters] = useState(EMPTY_FILTERS);

  const length = DEFAULT_PAGE_LENGTH;
  const searchTimer = useRef(null);

  const stateByTab = useCallback(
    (tabKey) => {
      const entry = states.find((s) => String(s.tab) === String(tabKey));
      return entry ? entry.value : states[0].value;
    },
    [states]
  );

  const buildParams = useCallback(
    (overrides = {}) => {
      const nextPage = overrides.page != null ? overrides.page : page;
      const stateFilter =
        overrides.state_filter != null
          ? overrides.state_filter
          : stateByTab(activeTab);
      const nextSearch = overrides.search != null ? overrides.search : search;

      return {
        search: nextSearch,
        state_filter: stateFilter,
        start: (nextPage - 1) * length,
        length,
        page: nextPage,
        draw: 1,
      };
    },
    [activeTab, search, page, length, stateByTab]
  );

  useEffect(() => {
    if (user) getCampaigns(buildParams());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  const changeTab = useCallback(
    (tabKey) => {
      setActiveTab(tabKey);
      setPage(1);
      setPageCampaigns(1);
      getCampaigns(
        buildParams({ state_filter: stateByTab(tabKey), page: 1 })
      );
    },
    [buildParams, getCampaigns, setPageCampaigns, stateByTab]
  );

  const changePage = useCallback(
    (nextPage) => {
      setPage(nextPage);
      setPageCampaigns(nextPage);
      getCampaigns(buildParams({ page: nextPage }));
    },
    [buildParams, getCampaigns, setPageCampaigns]
  );

  const onSearchChange = useCallback(
    (value) => {
      setSearch(value);
      if (searchTimer.current) clearTimeout(searchTimer.current);
      searchTimer.current = setTimeout(() => {
        setPage(1);
        setPageCampaigns(1);
        getCampaigns(buildParams({ search: value, page: 1 }));
      }, SEARCH_DEBOUNCE_MS);
    },
    [buildParams, getCampaigns, setPageCampaigns]
  );

  const applyFilters = useCallback((nextFilters) => {
    setFilters({ ...EMPTY_FILTERS, ...nextFilters });
  }, []);

  useEffect(
    () => () => {
      if (searchTimer.current) clearTimeout(searchTimer.current);
    },
    []
  );

  return {
    tabs: states,
    activeTab,
    search,
    filters,
    page: pageCampaigns || page,
    length,
    total: totalCampaigns || 0,
    campaigns: campaigns || [],
    loading: fetching,
    changeTab,
    changePage,
    onSearchChange,
    applyFilters,
  };
};

export default useCampaignsList;
